import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchMenus = createAsyncThunk(
  'menus/fetchMenus',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.getMenus(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMenu = createAsyncThunk(
  'menus/fetchMenu',
  async (menuId, { rejectWithValue }) => {
    try {
      const response = await api.getMenu(menuId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVendorMenus = createAsyncThunk(
  'menus/fetchVendorMenus',
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await api.getVendorMenus(vendorId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createMenu = createAsyncThunk(
  'menus/createMenu',
  async (menuData, { rejectWithValue }) => {
    try {
      const response = await api.createMenu(menuData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMenu = createAsyncThunk(
  'menus/updateMenu',
  async ({ menuId, menuData }, { rejectWithValue }) => {
    try {
      const response = await api.updateMenu(menuId, menuData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteMenu = createAsyncThunk(
  'menus/deleteMenu',
  async (menuId, { rejectWithValue }) => {
    try {
      await api.deleteMenu(menuId);
      return menuId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  menus: [],
  currentMenu: null,
  vendorMenus: {},
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {
    vendor_id: null,
    category: null,
    search: null,
    price_range: null,
    rating: null,
    availability: null,
  },
};

const menuSlice = createSlice({
  name: 'menus',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentMenu: (state, action) => {
      state.currentMenu = action.payload;
    },
    clearCurrentMenu: (state) => {
      state.currentMenu = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        vendor_id: null,
        category: null,
        search: null,
        price_range: null,
        rating: null,
        availability: null,
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    updateMenuInList: (state, action) => {
      const updatedMenu = action.payload;
      const index = state.menus.findIndex(m => m.id === updatedMenu.id);
      if (index !== -1) {
        state.menus[index] = updatedMenu;
      }
    },
    removeMenuFromList: (state, action) => {
      const menuId = action.payload;
      state.menus = state.menus.filter(m => m.id !== menuId);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Menus
      .addCase(fetchMenus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.data) {
          state.menus = data.data;
          state.pagination = {
            current: data.current_page || 1,
            pageSize: data.per_page || 10,
            total: data.total || 0,
          };
        } else {
          state.menus = Array.isArray(data) ? data : [];
        }
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Single Menu
      .addCase(fetchMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMenu = action.payload;
      })
      .addCase(fetchMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Vendor Menus
      .addCase(fetchVendorMenus.fulfilled, (state, action) => {
        const { vendorId, menus } = action.payload;
        state.vendorMenus[vendorId] = menus;
      })
      // Create Menu
      .addCase(createMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.menus.unshift(action.payload);
      })
      .addCase(createMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Menu
      .addCase(updateMenu.fulfilled, (state, action) => {
        const updatedMenu = action.payload;
        const index = state.menus.findIndex(m => m.id === updatedMenu.id);
        if (index !== -1) {
          state.menus[index] = updatedMenu;
        }
        if (state.currentMenu && state.currentMenu.id === updatedMenu.id) {
          state.currentMenu = updatedMenu;
        }
      })
      // Delete Menu
      .addCase(deleteMenu.fulfilled, (state, action) => {
        const menuId = action.payload;
        state.menus = state.menus.filter(m => m.id !== menuId);
        if (state.currentMenu && state.currentMenu.id === menuId) {
          state.currentMenu = null;
        }
      });
  },
});

export const {
  clearError,
  setCurrentMenu,
  clearCurrentMenu,
  setFilters,
  clearFilters,
  setPagination,
  updateMenuInList,
  removeMenuFromList,
} = menuSlice.actions;

export default menuSlice.reducer;
